import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { X, Pencil, Check, Copy, DoorOpen, DoorClosed, Clock } from 'lucide-react';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';
import { TraitStyleConfig, AttributeStyleConfig, IconStyleId, DeviceModalConfig } from '../../types';

// OccupancyTimeline component (fallback)
import { Style01_Timeline as OccupancyTimeline } from '../../attributes/OccupancyTimeline';

export interface DoorSensorDevice {
  deviceId: string;
  baseSN?: string;
  deviceName?: string;
  nickname?: string;
  location?: string;
  groupName?: string;
  isOnline?: boolean;
  detected?: boolean;        // true = open, false = closed
  openPercent?: number;      // Alternative: 100 = open, 0 = closed
  lastUpdated?: string | Date;
  traits?: string[];
  attributes?: Record<string, unknown>;
  system?: {
    endpointRelationship?: {
      classification?: string;
    };
  };
}

export interface DoorSensorModalProps {
  device: DoorSensorDevice;
  onClose: () => void;
  onSaveNickname?: (device: DoorSensorDevice, nickname: string | null) => Promise<void>;
  config?: Partial<DeviceModalConfig>;
  isMergedDevice?: boolean;
  onToggleMerge?: (baseSN: string) => void;
  // 24H Event history chart data (uses occupancyChartData for consistency with DeviceTemplateProps)
  occupancyChartData?: Array<{ time: string; timestamp: number; value: number; label?: string }>;
  isLoadingOccupancyChart?: boolean;
}

/**
 * DoorSensor Modal Template
 * 
 * 門感應器顯示 Modal (只讀，無控制)
 * - 開關狀態 (OPEN/CLOSE)
 * - 最後更新時間
 */
export const Template01_Status: React.FC<DoorSensorModalProps> = ({
  device,
  onClose,
  onSaveNickname,
  config,
  isMergedDevice = false,
  onToggleMerge,
  occupancyChartData = [],
  isLoadingOccupancyChart = false,
}) => {
  // Nickname editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(device.nickname || device.deviceName || '');
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const hiddenComponents: string[] = config?.hiddenComponents || [];

  // Sync editedName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && !isSavingRef.current && Date.now() > blockSyncUntilRef.current) {
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  // Device state
  const isOpen = device.detected ?? (device.openPercent !== undefined ? device.openPercent > 50 : false);
  const displayName = device.nickname || device.deviceName || device.deviceId;
  const displaySN = isMergedDevice ? device.baseSN : device.deviceId;

  // Multi-channel check
  const isMultiChannel = device.system?.endpointRelationship?.classification === 'multi_channel';

  // Format last updated time
  const formatLastUpdated = (date: string | Date | undefined): string => {
    if (!date) return '未知';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('zh-TW', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle nickname save
  const handleSaveNickname = useCallback(async () => {
    if (onSaveNickname && editedName !== (device.nickname || device.deviceName)) {
      isSavingRef.current = true;
      blockSyncUntilRef.current = Date.now() + 3000;
      await onSaveNickname(device, editedName || null);
      isSavingRef.current = false;
    }
    setIsEditingName(false);
  }, [device, editedName, onSaveNickname]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveNickname();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [handleSaveNickname, device.nickname, device.deviceName]);

  // Copy SN to clipboard
  const handleCopySN = useCallback(() => {
    navigator.clipboard.writeText(displaySN || '');
  }, [displaySN]);

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" 
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType="door_sensor" 
              isOn={isOpen}
              size={20}
              styleId={smallIconStyle}
            />
            {isEditingName ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                className="bg-slate-800 text-white px-2 py-1 rounded text-sm flex-1 min-w-0"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="text-white font-medium text-sm truncate">{displayName}</span>
            )}
            {isEditingName ? (
              <button onClick={handleSaveNickname} className="text-amber-400 hover:text-amber-300">
                <Check className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => setIsEditingName(true)} className="text-slate-400 hover:text-white">
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                online: device.isOnline,
                label: device.isOnline ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Big Icon - Status display */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center py-6">
              <div className={`w-32 h-32 rounded-full flex flex-col items-center justify-center transition-all duration-300 ${
                isOpen
                  ? 'bg-gradient-to-br from-amber-500/30 to-orange-500/30 border-2 border-amber-400/50 shadow-lg shadow-amber-500/30'
                  : 'bg-gradient-to-br from-green-500/20 to-teal-500/20 border-2 border-green-400/50 shadow-lg shadow-green-500/20'
              }`}>
                <DeviceIcon
                  deviceType="door_sensor"
                  isOn={isOpen}
                  size={48}
                  styleId={bigIconStyle}
                  className={isOpen ? 'text-amber-400 mb-2' : 'text-green-400 mb-2'}
                />
                <span className={`text-xl font-bold ${isOpen ? 'text-amber-400' : 'text-green-400'}`}>
                  {isOpen ? '開啟' : '關閉'}
                </span>
              </div>
            </div>
          )}

          {/* Last updated */}
          {device.lastUpdated && (
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-400">最後更新</span>
              </div>
              <div className="mt-2 text-lg text-white">
                {formatLastUpdated(device.lastUpdated)}
              </div>
            </div>
          )}

          {/* 24H Occupancy Timeline */}
          <OccupancyTimeline
            data={occupancyChartData}
            loading={isLoadingOccupancyChart}
            title="過去 24 小時開關統計"
            activeLabel="開啟"
            inactiveLabel="關閉"
            activeColor="#f59e0b"
            inactiveColor="#374151"
          />
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-800/50 space-y-3">
          {/* Multi-channel toggle */}
          {isMultiChannel && onToggleMerge && device.baseSN && (
            <div className="flex items-center justify-center gap-3 py-2">
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  isMergedDevice 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                單一方塊
              </button>
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  !isMergedDevice 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                個別方塊
              </button>
            </div>
          )}

          {/* Location and SN */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{displaySN}</span>
              <button onClick={handleCopySN} className="text-slate-400 hover:text-white">
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Status;
